/**
 * O balde da fila de correção quando o motivo é "o valor que a planilha
 * trouxe não casa com o cadastro" (2026-10-08).
 *
 * Esse balde é o trio (dimensão, valor, marca) e não tem canal — por isso
 * a chave de lista da tela, que era o canal, não serve mais: todo balde de
 * valor tem canal nulo e colidiria com o "sem canal identificado". A
 * lógica mora aqui, pura e testada, e a página só desenha.
 */

import type {
  CorrectionBucket,
  CorrectionGroup,
  ImportDimension
} from "@/shared/api/api-client";
import { canonicalFieldLabel } from "@/shared/vocabulary/canonical-fields";
import { VEHICLE_KIND_LABELS } from "@/shared/vocabulary/vehicle-kinds";

export function isImportGroup(group: CorrectionGroup): boolean {
  return group.import_dimension !== null && group.import_dimension !== undefined;
}

/** Chave estável de lista: (canal, motivo) pro balde de canal, o trio pro
 *  de valor. Prefixos diferentes pra um canal chamado "themes" nunca
 *  colidir. O motivo entrou em 2026-10-19: o mesmo canal pode ter um
 *  balde por motivo. */
export function groupKey(group: CorrectionGroup): string {
  if (isVehicleAmbiguous(group)) {
    // Nome + UF + tipo da linha (2026-11-03): "Band" de SP e do RJ são
    // dois baldes, cada um com a sua escolha.
    return `veiculo:${group.channel ?? ""}|${group.vehicle_state_key ?? "-"}|${group.vehicle_kind ?? "-"}`;
  }
  if (!isImportGroup(group)) return `canal:${group.channel ?? ""}|${group.failure_kind ?? "-"}`;
  return `valor:${group.import_dimension}|${group.import_brand_id ?? ""}|${group.import_value ?? ""}`;
}

/** O que viaja na ação do grupo (reprocessar): o balde, nada além. O de
 *  canal leva o motivo, senão o servidor reprocessaria o canal inteiro,
 *  inclusive as linhas de outro motivo que estão em outra aba. */
export function bucketOf(group: CorrectionGroup): CorrectionBucket {
  if (isVehicleAmbiguous(group)) {
    return {
      channel: group.channel,
      failure_kind: group.failure_kind,
      vehicle_state_key: group.vehicle_state_key ?? null,
      vehicle_kind: group.vehicle_kind ?? null
    };
  }
  if (!isImportGroup(group)) return { channel: group.channel, failure_kind: group.failure_kind };
  return {
    channel: null,
    import_dimension: group.import_dimension,
    import_value: group.import_value,
    import_brand_id: group.import_brand_id
  };
}

/** O balde de VEÍCULO AMBÍGUO (2026-11-03): mais de um veículo com o
 *  nome, e a UF e o tipo da planilha não separaram. */
export function isVehicleAmbiguous(group: CorrectionGroup): boolean {
  return group.failure_kind === "vehicle_ambiguous";
}

/** "UF SP · TV" — o que a planilha trouxe, que é o resto da chave do
 *  balde ambíguo; "sem UF nem tipo na planilha" quando não trouxe nada. */
export function vehicleHint(group: CorrectionGroup): string {
  const parts = [
    group.vehicle_state_key ? `UF ${group.vehicle_state_key}` : null,
    group.vehicle_kind ? VEHICLE_KIND_LABELS[group.vehicle_kind] : null
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "sem UF nem tipo na planilha";
}

/** O nome que a pessoa reconhece: a grafia da planilha, e só na falta
 *  dela a chave normalizada. */
export function importValueName(group: CorrectionGroup): string {
  return group.import_label ?? group.import_value ?? "";
}

const NOUN: Record<ImportDimension, string> = {
  polarity: "polaridade",
  approach: "abordagem",
  themes: "subtema",
  key_messages: "mensagem-chave"
};

/** "Tema “Economia Verde” · marca Corsan" — a marca só nas dimensões de
 *  catálogo por marca, e só quando a empresa tem mais de uma. */
export function importGroupTitle(group: CorrectionGroup, brandName?: string | null): string {
  const dimension = group.import_dimension as ImportDimension;
  const noun = NOUN[dimension] ?? canonicalFieldLabel(dimension).toLowerCase();
  const title = `${noun.charAt(0).toUpperCase()}${noun.slice(1)} “${importValueName(group)}”`;
  return brandName ? `${title} · marca ${brandName}` : title;
}

/** Tema e mensagem-chave se CRIAM no cadastro; polaridade e abordagem são
 *  vocabulário fixo — só se escolhe. */
export function canCreateInCatalog(group: CorrectionGroup): boolean {
  return group.import_dimension === "themes" || group.import_dimension === "key_messages";
}

export type SettingsTarget = { tab: "temas" | "mensagens"; params: Record<string, string> };

/** Pra onde "Criar no cadastro" leva: a aba, com o nome (e a marca, e a
 *  fonte da mensagem) já preenchidos. `null` quando não se cria. */
export function createTarget(
  group: CorrectionGroup,
  postSource?: "press" | "network" | null
): SettingsTarget | null {
  if (!canCreateInCatalog(group)) return null;
  const params: Record<string, string> = { novo: importValueName(group) };
  if (group.import_brand_id !== null && group.import_brand_id !== undefined) {
    params.marca = String(group.import_brand_id);
  }
  if (group.import_dimension === "key_messages" && postSource) {
    params.fonte = postSource;
  }
  return {
    tab: group.import_dimension === "themes" ? "temas" : "mensagens",
    params
  };
}
